/**
 * Event Schema Components
 * From SchemaMarkup.tsx lines 1108-1243
 * For upcoming events and experiences
 */

import { BASE_URL, SEO } from '@/lib/constants';
import type { EventSchemaProps } from './types';
import { parseEventDate } from './utils';

/**
 * Single Event Schema
 */
export function EventSchema({
  name,
  description,
  startDate,
  endDate,
  location,
  image,
  url,
  eventType = 'SportsEvent',
  organizer = SEO.siteName,
}: EventSchemaProps) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': eventType,
    name,
    description,
    startDate,
    ...(endDate && { endDate }),
    location: location ? {
      '@type': 'Place',
      name: location,
      address: {
        '@type': 'PostalAddress',
        addressCountry: 'CL',
      },
    } : undefined,
    ...(image && { image }),
    ...(url && { url: `${BASE_URL}${url}` }),
    organizer: {
      '@type': 'Organization',
      name: organizer,
      url: BASE_URL,
    },
    performer: {
      '@type': 'Organization',
      name: organizer,
    },
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    offers: {
      '@type': 'Offer',
      availability: 'https://schema.org/LimitedAvailability',
      url: `${BASE_URL}/contact/`,
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

/**
 * Multiple Events Schema for events page
 */
export function EventsListSchema({
  events,
}: {
  events: Array<{ title: string; description: string; date: string; category: string; image?: string }>;
}) {
  const schemas = events.map((event) => ({
    '@context': 'https://schema.org',
    '@type': event.category === 'Fishing' ? 'Event' : 'SportsEvent',
    name: event.title,
    description: event.description,
    startDate: parseEventDate(event.date),
    location: {
      '@type': 'Place',
      name: 'Chile',
      address: {
        '@type': 'PostalAddress',
        addressCountry: 'CL',
      },
    },
    ...(event.image && { image: event.image }),
    organizer: {
      '@type': 'Organization',
      name: SEO.siteName,
      url: BASE_URL,
    },
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    offers: {
      '@type': 'Offer',
      availability: 'https://schema.org/LimitedAvailability',
      url: `${BASE_URL}/contact/`,
    },
  }));

  return (
    <>
      {schemas.map((schema, index) => (
        <script
          key={index}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      ))}
    </>
  );
}
